<template>
  <div :style="{ minHeight: '250px', maxWidth: '900px' }">
    <b-card
      border-variant="primary"
      header-bg-variant="primary"
      header-text-variant="white"
      header="Upload a CSV of query data to a page:"
    >
      <Zip v-model="upload" />
      <div v-if="upload.url" class="mt-2 text-small text-muted">
        <template v-if="$apollo.queries.fetch_url.loading">
          <b-spinner small /> Crawling
        </template>
        <template v-else> {{ upload.queries.length }} queries to </template>
        <b-link :href="upload.url" target="_blank" class="text-muted">
          <span class="page_url">{{ upload.url }}</span>
          <b-icon-box-arrow-up-right class="ml-1" />
        </b-link>
      </div>
      <div v-else class="mt-2">
        <p>
          <strong>Export from Search Console, Moz, etc.</strong>
          We attempt to match these columns from any zipped or plain CSV:
          <code>url</code>,&nbsp;<code>keyword/query</code>,
          <code>volume/clicks</code>
        </p>
        Or <a href="/pricing" target="_parent">sign up for our free trial</a> to
        import your site data automatically.
      </div>
    </b-card>
    <b-alert v-if="page.status_code && page.status_code > 400">
      {{page.status_code}} error. Unable to reach this URL.
    </b-alert>
    <b-row v-if="showTitle" class="py-4">
      <b-col>
        <div class="mb-2"><small>Title Editor</small></div>
        <TitleEditor
          v-if="showTitle"
          ref="title"
          :account="{}"
          :page="page"
          :site="{}"
          :isDemo="true"
          @update="title = $event"
          :autofocus="true"
        />
      </b-col>
      <b-col cols="3">
        <b-row>
          <b-col>
            <small>Title Score</small>
            <TitleScore :score="title.score" :width="65" :height="65" />
          </b-col>
          <b-col class="text-muted">
            <small>Link Score</small>
            <TitleScore
              :score="0"
              :width="65"
              :height="65"
              label="🔒"
              color="dark"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row v-if="showTitle" class="py-4">
      <b-col>
        <div class="mb-2"><small>Score Factors</small></div>
        <TitleReasons
          v-if="showTitle"
          :site="{}"
          :page="page"
          :title="title"
          :is_demo="true"
          @update="$refs.title.update()"
        />
        <template v-if="title.reasons">
          <b-card
            v-for="reason of lockedReasons"
            :key="reason"
            no-body
            class="mb-1"
          >
            <b-card-header class="text-muted"> 🔒 {{ reason }} </b-card-header>
          </b-card>
        </template>
      </b-col>
      <b-col cols="3">
        <PageWords
          :page="page"
          :query_words="title.query_words"
          @refresh="refresh(true)"
          :isDemo="true"
        />
      </b-col>
    </b-row>
  </div>
</template>
<style lang="scss">
html {
  overflow: hidden;
}
body {
  background-color: transparent !important;
}
.col-1.col-score {
  text-align: center;
  flex: 0 0 10%;
  max-width: 10%;
  margin-top: 4px;
  padding-right: 0;
}
.word-list-item {
  font-size: 0.875em;
}
.word-list-item-progress {
  width: 50px;
}
.title-container {
  border-radius: 2px;
  background: white;
}
</style>
<script>
import _ from 'lodash'
import Zip from '@/components/Zip'
import TitleScore from '@/components/TitleScore'
import TitleEditor from '@/components/TitleEditor'
import TitleReasons from '@/components/TitleReasons'
import PageWords from '@/components/PageWords'
import FETCH_URL_QUERY from '@/graphql/FetchUrlQuery.gql'
import { BIconBoxArrowUpRight } from 'bootstrap-vue'
export default {
  name: 'Demo',
  components: {
    Zip,
    TitleScore,
    TitleEditor,
    TitleReasons,
    PageWords,
    BIconBoxArrowUpRight
  },
  props: [],

  data: () => {
    return {
      upload: {}, //"url":"https://siteseer.io/title-tag-tool/","queries":[{"query":"title optimization tool","clicks":2,"impressions":39,"ctr":0.0513,"position":11.26}]},
      title: {},
      fetch_url: {},
      lockedReasons: [
        'Google SERP Rewrite Check',
        'Duplicate Title Check',
        'Boilerplate Recommendations'
      ],
      error: null
    }
  },

  apollo: {
    fetch_url: {
      query: FETCH_URL_QUERY,
      variables() {
        return { url: this.upload.url }
      },
      skip() {
        return !this.upload.url
      },
      fetchPolicy: 'cache-first'
    }
  },
  mounted() {
    new ResizeObserver(function () {
      window.parent.postMessage(['setHeight', document.body.clientHeight], '*')
    }).observe(document.body)
  },

  computed: {
    showTitle() {
      return this.upload.url
    },
    page() {
      if (!this.upload.url) {
        return {}
      }
      return {
        ...this.upload,
        id: '00000000-0000-0000-0000-000000000000',
        site_id: 1,
        site: {
          id: 1,
          total_pages: 1
        },
        title: this.fetch_url.title,
        status_code: this.fetch_url.status_code,
        query_words: [],
        page_issues: []
      }
    }
  },
  methods: {
    async refresh() {
      this.$emit('refresh')
    },
    // Emitted by TitleEditor when the title is changed/saved
    async onTitleUpdate(val) {
      this.title = val
      await this.$nextTick()
      if (this.title.score != this.page.title_score) {
        this.$emit('refresh')
      }
      this.$emit('titleOffset', this.$refs.TitleEditor.$el.offsetTop)
    },
    // Used to call Update within TitleEditor
    triggerTitleUpdate(val) {
      this.$refs.TitleEditor.save(val)
    },
    focus() {
      const $vm = this
      setTimeout(
        function () {
          $vm.$refs.TitleEditor.focus()
        },
        $vm.$refs.TitleEditor.focus ? 0 : 100
      )
    }
  }
}
</script>